/* eslint-disable indent */
import React, { HTMLAttributes, useEffect } from 'react';
import { TileSelectGroup, TileSelect, Layout } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { FieldError, TrackableComponent } from '..';
import _ from 'lodash';
import { QuestionItem } from '../../lib/types/QuestionItem';
import { bronsonIconSwitchEmpty } from '../../lib/helpers/icon-helpers';
import { TileSelectWithImage } from './items/TileSelectWithImage';
import { MatchMakerAnswers } from '../../../shared/types';

type OwnProps = {
  title?: string;
  itemColumnCount?: number[];
  itemList: QuestionItem<string | number>[];
  'data-testid'?: string;
  propName: keyof MatchMakerAnswers;
  propObject: object;
  maxChoices?: number;
  maxChoicesWarning?: string;
  displayDescription?: boolean;
  preserveValues?: boolean;
  showErros?: boolean;
  onPostChange?: () => void;
  required?: boolean;
  inputType?: 'checkbox' | 'radio';
} & HTMLAttributes<HTMLDivElement>;

/** The array itemColumnCount takes two numbers:
 *  - the first to assign the grid for desktop
 *  - the second one to assign the grid for smaller resolutions (s and down)
 */
export const SelectableTileGroup = ({
  title = '',
  itemColumnCount,
  itemList,
  propName,
  maxChoices,
  maxChoicesWarning,
  displayDescription = true,
  preserveValues = false,
  showErros = true,
  required,
  inputType = 'checkbox',
  ...restProps
}: OwnProps): JSX.Element => {
  const newItemList = _.filter(itemList, 'availableIn');
  const groupName = `selectableTileGroup-${propName}`;
  const groupTestId = (restProps['data-testid'] as string) || `selectableTileGroup-${propName}`;
  const groupErrorId = `error-${groupTestId}`;

  const { values, setFieldValue, touched, setFieldTouched, errors } =
    useFormikContext<MatchMakerAnswers>();

  const toValueArray = (fieldValue: MatchMakerAnswers[keyof MatchMakerAnswers]) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue as (string | number | boolean)[];
    }

    if (_.isNil(fieldValue) || fieldValue === '') {
      return [];
    }

    return [fieldValue as string | number | boolean];
  };

  const selectedValues = toValueArray(values[propName]);

  const isSameValue = (value: string | number | boolean, candidate: string | number) =>
    String(value) === String(candidate);

  const isValueSelected = (value: string | number) =>
    selectedValues.some((selectedValue) => isSameValue(selectedValue, value));

  const getItemValue = (rawValue: string) =>
    newItemList.find((item) => String(item.value) === rawValue)?.value ?? rawValue;

  const shouldLimitChoices = inputType === 'checkbox' && !!maxChoices;

  useEffect(() => {
    // This is to remove the old values that do not exist in the new items
    // anymore to prevent having `dirty values` in database that results in
    // weird behavior in frontend
    const validValues = newItemList.map((item) => item.value);
    const currentValues = toValueArray(values[propName]);
    const validCurrentValues = currentValues.filter((value) =>
      validValues.some((validValue) => isSameValue(value, validValue))
    );

    if (preserveValues) return;

    setFieldValue(
      propName,
      inputType === 'radio' ? validCurrentValues.slice(0, 1) : validCurrentValues
    );
  }, []);

  const layoutItemClassName = itemColumnCount
    ? `${`u-${itemColumnCount[0]}/12`}${' '}
  ${`u-${itemColumnCount[1]}/12@s`}${' '}
  ${itemColumnCount.length > 2 ? `u-${itemColumnCount[2]}/12@xs` : ''}`
    : '';

  const handleClickItem = () => setFieldTouched(propName, true);

  const handleChangeItem = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const targetValue = getItemValue(target.value);

    if (inputType === 'radio') {
      setFieldValue(propName, target.checked ? [targetValue] : []);
      return;
    }

    const copiedValueArray = target.checked
      ? isValueSelected(targetValue)
        ? selectedValues
        : [...selectedValues, targetValue]
      : selectedValues.filter((value) => !isSameValue(value, targetValue));

    setFieldValue(propName, copiedValueArray);
  };

  const handleBlurItem = () => setFieldTouched(propName, true);

  const selectTileProps =
    inputType === 'checkbox' ? { checkboxGroupName: groupName } : { radioGroupName: groupName };

  return (
    <>
      <TileSelectGroup
        aria-required={restProps['aria-required']}
        layoutItemClassName={layoutItemClassName}
        testId={groupTestId}
        data-testid={groupTestId}
        aria-describedby={groupErrorId}
        title={{ text: '' }}
        required={required}
      >
        {newItemList.map((item) => {
          const disabledField =
            shouldLimitChoices &&
            selectedValues.length >= maxChoices &&
            !isValueSelected(item.value);

          return (
            item.availableIn && (
              <TrackableComponent
                key={`${propName}-${item.value}-wrapper`}
                trackingComponent={inputType === 'checkbox' ? 'checkbox' : 'radiobutton'}
                trackingFunction={item.trackingName || ''}
              >
                {item.img ? (
                  <TileSelectWithImage
                    key={`${propName}-${item.value}`}
                    id={`${propName}-${String(item.value)}`}
                    name={inputType === 'radio' ? groupName : `${propName}-${String(item.value)}`}
                    item={item}
                    title={<span className="u-font-size-fs0">{title || item.displayName}</span>}
                    checked={isValueSelected(item.value)}
                    disabled={disabledField}
                    displayDescription={displayDescription}
                    onClick={handleClickItem}
                    onChange={handleChangeItem}
                    onBlur={handleBlurItem}
                  />
                ) : (
                  <TileSelect
                    className="u-font-size-fs0"
                    key={`${propName}-${item.value}`}
                    id={`${propName}-${String(item.value)}`}
                    title={<span className="u-font-size-fs0">{title || item.displayName}</span>}
                    value={item.value.toString()}
                    icon={item.icon || ''}
                    checked={isValueSelected(item.value)}
                    disabled={disabledField}
                    aria-disabled={disabledField ? 'true' : 'false'}
                    onClick={handleClickItem}
                    onChange={handleChangeItem}
                    onBlur={handleBlurItem}
                    inputType={inputType}
                    {...selectTileProps}
                  >
                    {displayDescription ? item.description : null}
                  </TileSelect>
                )}
              </TrackableComponent>
            )
          );
        })}
      </TileSelectGroup>

      <Layout>
        <Layout.Item>
          {showErros && touched[propName] && errors[propName] && (
            <FieldError id={groupTestId} errorMessage={(errors[propName] as string) || ''} />
          )}

          {shouldLimitChoices && maxChoicesWarning && selectedValues.length >= maxChoices && (
            <p
              className={`u-mt-small u-mb-none ${bronsonIconSwitchEmpty(
                'u-text-fs-1',
                'u-text-fs-2',
                'u-text-fs-2',
                'u-text-fs-1',
                'u-text-fs-1'
              )} `}
            >
              {maxChoicesWarning}
            </p>
          )}
        </Layout.Item>
      </Layout>
    </>
  );
};
